function parseBoolean(value) {
  return value === true || String(value).toLowerCase() === 'true';
}

function parseTimestamp(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseFiniteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatDamage(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0.00';
  return (Math.abs(numeric) < 0.005 ? 0 : numeric).toFixed(2);
}

function formatCompactDamage(value) {
  return formatDamage(value).replace(/\.?0+$/, '');
}

export function normalizeCombatEvent(evt, receivedAtMs = Date.now()) {
  if (!evt?.EventType) return null;

  const details = evt.Details || {};
  const sentAtMs = parseTimestamp(evt.SentAtMs);
  const createdAtMs = parseTimestamp(evt.CreatedAt);
  const resolvedAtMs = parseTimestamp(details.resolvedAtMs) ?? createdAtMs;
  const id = evt.Id || `${evt.EventType}-${createdAtMs ?? receivedAtMs}-${JSON.stringify(details)}`;

  if (evt.EventType === 'DealDmg') {
    const attackerIsPlayer = parseBoolean(details.attackerIsPlayer);
    const isCrit = parseBoolean(details.isCrit);
    const isDodge = parseBoolean(details.isDodge);
    const amount = parseFiniteNumber(details.dmg);
    const kind = isDodge ? 'dodge' : (isCrit ? 'crit' : 'hit');

    return {
      id,
      kind,
      attacker: attackerIsPlayer ? 'player' : 'enemy',
      target: attackerIsPlayer ? 'enemy' : 'player',
      amount: amount ?? 0,
      breakdown: {
        baseDamage: parseFiniteNumber(details.baseDamage),
        damageBeforeArmor: parseFiniteNumber(details.damageBeforeArmor),
        armor: parseFiniteNumber(details.armor),
        armorReduction: parseFiniteNumber(details.armorReduction),
        hpLost: parseFiniteNumber(details.hpLost),
        armorApplied: details.armorApplied === undefined
          ? null
          : parseBoolean(details.armorApplied),
        damageContext: details.damageContext || null,
      },
      occurredAt: resolvedAtMs ?? receivedAtMs,
      receivedAtMs,
      sentAtMs,
      timing: {
        attackIntervalMs: details.attackIntervalMs,
        nextAttackAtMs: details.nextAttackAtMs,
      },
      isFresh: !Number.isFinite(sentAtMs)
        || !Number.isFinite(resolvedAtMs)
        || sentAtMs - resolvedAtMs <= 1000,
    };
  }

  if (evt.EventType === 'RegenProgress' || evt.EventType === 'ConsumeItem') {
    const healed = Number(details.healed ?? details.Healed);
    if (!Number.isFinite(healed) || healed <= 0) return null;
    return {
      id,
      kind: 'heal',
      attacker: null,
      target: 'player',
      amount: healed,
      occurredAt: createdAtMs ?? receivedAtMs,
      receivedAtMs,
      sentAtMs,
      timing: null,
      source: evt.EventType === 'ConsumeItem' ? 'consumable' : 'regeneration',
      isFresh: !Number.isFinite(sentAtMs)
        || !Number.isFinite(createdAtMs)
        || sentAtMs - createdAtMs <= 1000,
    };
  }

  if (evt.EventType === 'FleeLog') {
    const message = typeof details.message === 'string'
      ? details.message
      : '';
    const result = String(
      details.result
      || (message.toLowerCase().includes('success') ? 'success' : 'fail'),
    ).toLowerCase();
    return {
      id,
      kind: 'flee',
      result,
      attempt: parseFiniteNumber(details.attempt),
      message,
      occurredAt: resolvedAtMs ?? receivedAtMs,
      receivedAtMs,
      sentAtMs,
      timing: {
        attemptIntervalMs: details.attemptIntervalMs,
        nextAttemptAtMs: details.nextAttemptAtMs,
      },
      isFresh: !Number.isFinite(sentAtMs)
        || !Number.isFinite(resolvedAtMs)
        || sentAtMs - resolvedAtMs <= 1000,
    };
  }

  if (evt.EventType === 'UpdateUserAction'
    && ['FIGHTING', 'FLEEING'].includes(details.userAction)
    && details.userCombat) {
    try {
      const userCombat = JSON.parse(details.userCombat);
      return {
        id,
        kind: 'snapshot',
        attacker: null,
        target: null,
        amount: 0,
        occurredAt: parseTimestamp(details.combatSnapshotAtMs) ?? createdAtMs ?? receivedAtMs,
        receivedAtMs,
        sentAtMs,
        action: details.userAction,
        userCombat,
        isFresh: true,
      };
    } catch {
      return null;
    }
  }

  return null;
}

export function formatCombatLogEntry(event) {
  if (!event) return null;

  if (event.kind === 'dodge') {
    return event.attacker === 'player'
      ? 'Enemy DODGED your attack!'
      : 'You DODGED the attack!';
  }

  if (event.kind !== 'hit' && event.kind !== 'crit') return null;

  const damage = parseFiniteNumber(event.amount) ?? 0;
  const actorText = event.attacker === 'player'
    ? `You dealt ${formatCompactDamage(damage)} damage`
    : `Enemy dealt ${formatCompactDamage(damage)} damage to you`;
  const breakdown = event.breakdown || {};
  const baseDamage = parseFiniteNumber(breakdown.baseDamage);
  const damageBeforeArmor = parseFiniteNumber(breakdown.damageBeforeArmor);
  const armor = parseFiniteNumber(breakdown.armor);
  const armorReduction = parseFiniteNumber(breakdown.armorReduction);
  const hpLost = parseFiniteNumber(breakdown.hpLost);
  const armorApplied = breakdown.armorApplied !== false;
  const critBonus = baseDamage === null || damageBeforeArmor === null
    ? null
    : damageBeforeArmor - baseDamage;
  const hasCritModifier = event.kind === 'crit'
    && critBonus !== null
    && Math.abs(critBonus) >= 0.005;
  const hasLegacyCrit = event.kind === 'crit' && critBonus === null;
  const hasArmorReduction = armorApplied
    && armor !== null
    && armor >= 0.005
    && armorReduction !== null
    && armorReduction >= 0.005;
  const hasBypassedArmor = !armorApplied && armor !== null && armor >= 0.005;
  const overkill = hpLost === null ? 0 : Math.max(0, damage - hpLost);
  const hasOverkill = overkill >= 0.005;
  const critText = hasCritModifier || hasLegacyCrit ? ' — CRITICAL!' : '';
  const details = [];

  if (hasCritModifier || hasArmorReduction || hasBypassedArmor) {
    const calculationBase = baseDamage ?? damageBeforeArmor ?? damage;
    let equation = `${formatDamage(calculationBase)} base`;

    if (hasCritModifier) {
      const operation = critBonus < 0 ? '−' : '+';
      equation += ` ${operation} ${formatDamage(Math.abs(critBonus))} crit bonus`;
    }
    if (hasArmorReduction) {
      equation += ` − ${formatDamage(armorReduction)} blocked by ${formatDamage(armor)} Armor`;
    } else if (hasBypassedArmor) {
      const context = breakdown.damageContext === 'fleeFailure' ? ' while fleeing' : '';
      equation += `; ${formatDamage(armor)} Armor not applied${context}`;
    }
    equation += ` = ${formatDamage(damage)} damage`;
    details.push(equation);
  }

  if (hasOverkill) {
    details.push(`${formatDamage(hpLost)} HP lost, ${formatDamage(overkill)} overkill`);
  }

  return `${actorText}${critText}${details.length > 0 ? ` (${details.join('; ')})` : ''}`;
}

export function formatCombatOutcome(details) {
  const outcome = String(details?.outcome || '').toLowerCase();
  const enemyName = typeof details?.enemyName === 'string'
    ? details.enemyName.trim()
    : '';

  if (outcome === 'victory') {
    return enemyName
      ? `Fight won — you defeated ${enemyName}.`
      : 'Fight won.';
  }

  if (outcome === 'defeat') {
    return enemyName
      ? `Fight lost — ${enemyName} defeated you.`
      : 'Fight lost.';
  }

  if (outcome === 'fled') {
    return enemyName
      ? `Flee successful — you escaped from ${enemyName}.`
      : 'Flee successful.';
  }

  return null;
}

function effectGroup(kind) {
  if (kind === 'hit' || kind === 'crit') return 'damage';
  return kind;
}

const COMBAT_VISUAL_DURATIONS = Object.freeze({
  hit: Object.freeze({ attack: 260, reaction: 320, feedback: 850 }),
  crit: Object.freeze({ attack: 260, reaction: 480, feedback: 1000 }),
  dodge: Object.freeze({ attack: 260, reaction: 380, feedback: 850 }),
  heal: Object.freeze({ attack: 0, reaction: 600, feedback: 850 }),
});

export function getCombatVisualTiming(kind) {
  const durations = COMBAT_VISUAL_DURATIONS[kind] ?? COMBAT_VISUAL_DURATIONS.hit;
  const reactionDelayMs = durations.attack;
  const feedbackDelayMs = reactionDelayMs + durations.reaction;

  return {
    attackDurationMs: durations.attack,
    reactionDelayMs,
    reactionDurationMs: durations.reaction,
    feedbackDelayMs,
    feedbackDurationMs: durations.feedback,
    totalDurationMs: feedbackDelayMs + durations.feedback,
  };
}

export function enqueueCombatEffect(activeEffects, event, sequence, now = Date.now()) {
  const nextEffect = {
    ...event,
    sequence,
    count: 1,
    expiresAt: now + getCombatVisualTiming(event.kind).totalDurationMs,
  };
  const group = effectGroup(event.kind);
  const matching = activeEffects.filter(effect => (
    effect.target === event.target && effectGroup(effect.kind) === group
  ));
  if (matching.length < 4) return [...activeEffects, nextEffect];

  const combined = [...matching, nextEffect];
  const combinedKind = group === 'damage' && combined.some(effect => effect.kind === 'crit')
    ? 'crit'
    : event.kind;
  const combinedAmount = combined.reduce((sum, effect) => sum + Number(effect.amount || 0), 0);
  const combinedCount = combined.reduce((sum, effect) => sum + Number(effect.count || 1), 0);
  const matchingIds = new Set(matching.map(effect => effect.id));

  return [
    ...activeEffects.filter(effect => !matchingIds.has(effect.id)),
    {
      ...nextEffect,
      id: `burst-${event.target}-${event.id}`,
      kind: combinedKind,
      amount: combinedAmount,
      count: combinedCount,
      expiresAt: now + getCombatVisualTiming(combinedKind).totalDurationMs,
    },
  ];
}
